import { FontAwesome5, Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Formik } from "formik";
import { Box, Button, HStack, Icon, Image, Pressable, ScrollView, Spinner, Text, useTheme, VStack } from "native-base";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Linking, SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes, Scale, VerticalScale } from "../../constants";
import { calculateDailyTotalForMacro } from "../../helpers/coachHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../../helpers/featureFlags";
import { getOrganisation } from "../../helpers/userHelpers";
import {
  DietPreferences,
  onboardingDataSelector,
  onboardingSlice,
  setMenstrualCycle,
} from "../../slices/onboardingSlice";
import { diyFormSelector, userSelector } from "../../slices/userSlice";
import { type MacrosPreference, GenderEnum, MacrosPreferenceEnum } from "../../types";
import { createOnboardingState } from "./createOnboardingState";
import { updateMacrosInState } from "./onboardingHelpers";

interface NutritionalPlanScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

interface Plan {
  label: string;
  description: string;
  value: MacrosPreference;
  emoji: string;
  testID: string;
  moreInfoUrl?: string;
}

const NutritionalPlanScreen: React.FC<NutritionalPlanScreenProps> = ({ navigation }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const diyFormValues = useSelector(diyFormSelector);
  const onboardingData = useSelector(onboardingDataSelector);
  const user = useSelector(userSelector);

  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  const isPlantBasedApp = isFeatureFlagActive(user, FeatureFlag.FF_PLANT_BASED_APP);
  const disableLowCarbOption = isFeatureFlagActive(user, FeatureFlag.FF_DISABLE_LOW_CARB_OPTION);
  const disableKetoOption = isFeatureFlagActive(user, FeatureFlag.FF_DISABLE_KETO_OPTION);

  const [isThisTheFirstScreenOfTheOnboardingTheUserIsSeeing, setIsThisTheFirstScreenOfTheOnboardingTheUserIsSeeing] =
    React.useState(false);

  useEffect(() => {
    if (!diyFormValues?.mss) {
      if (!user) {
        return;
      }
      if (!user.intake) {
        throw new Error("diyFormValues.mss is not set");
      }

      dispatch(onboardingSlice.actions.setUserProfileData(user.intake));
      updateMacrosInState(dispatch, createOnboardingState(user.intake));

      // NOTE: If this user refreshes the app then this will not trigger again and thus they will see a back button.
      // But it doesn't cause a big problem because they can just select the tab from the bottom to come back.
      setIsThisTheFirstScreenOfTheOnboardingTheUserIsSeeing(true);

      // TODO: Note that this code does not work but gives an idea of how to do it
      // // This allows the user to use the back button to go back to the previous screens
      // const previousScreens = [
      //   Routes.DIY1GoalScreen,
      //   Routes.ActivityLevelScreen,
      //   Routes.BiometricDataScreen,
      //   Routes.PhysicalStatsScreen,
      //   Routes.WeeklyGoalScreen,
      // ];
      // previousScreens.reverse().forEach((screenName) => {
      //   console.log(`Pushing screen: ${screenName}`);
      //   navigation.dispatch(StackActions.push(screenName));
      // });
      // console.log("Pushed screens");
    }
  }, []);

  const isFemale = onboardingData.biometricData.gender === GenderEnum.FEMALE;
  const isFemaleHormonesEnabled = isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES);
  const isMenopauseOnlyEnabled = isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES_MENOPAUSE_ONLY);

  const validationSchema = Yup.object().shape({
    selectedPlan: Yup.mixed<MacrosPreference>().required(
      t("onboarding.onboarding_7_nutrition_plan_screen.validation_error")
    ),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  const plans: Plan[] = [];

  // Add menopause and cycle diet plans based on feature flags
  if (isFemale) {
    if (isMenopauseOnlyEnabled) {
      // Only show Menopause plan if FF_FEMALE_HORMONES_MENOPAUSE_ONLY is active
      plans.push({
        label: t("onboarding.onboarding_7_nutrition_plan_screen.menopause"),
        description: t("onboarding.onboarding_7_nutrition_plan_screen.menopause_description"),
        value: MacrosPreferenceEnum.MENOPAUSE,
        emoji: "🌸",
        testID: `nutrition-plan-option-${MacrosPreferenceEnum.MENOPAUSE}`,
        // NOTE: We previously did this only for the Killerbody app. Now we are doing it generically.
        // So we need an Intercom article for this instead.
        // moreInfoUrl: "https://killerbodyfood.com/blogs/voedingstips/tagged/menopause",
      });
    } else if (isFemaleHormonesEnabled) {
      // Show both Cycle Diet and Menopause plans if FF_FEMALE_HORMONES is active (and MENOPAUSE_ONLY is not)
      plans.push(
        {
          label: t("onboarding.onboarding_7_nutrition_plan_screen.cycle_diet"),
          description: t("onboarding.onboarding_7_nutrition_plan_screen.cycle_diet_description"),
          value: MacrosPreferenceEnum.CYCLE_DIET,
          emoji: "🔄",
          testID: `nutrition-plan-option-${MacrosPreferenceEnum.CYCLE_DIET}`,
          moreInfoUrl: "https://killerbodyfood.com/blogs/voedingstips/tagged/menstruatiecyclus",
        },
        {
          label: t("onboarding.onboarding_7_nutrition_plan_screen.menopause"),
          description: t("onboarding.onboarding_7_nutrition_plan_screen.menopause_description"),
          value: MacrosPreferenceEnum.MENOPAUSE,
          emoji: "🌸",
          testID: `nutrition-plan-option-${MacrosPreferenceEnum.MENOPAUSE}`,
          moreInfoUrl: "https://killerbodyfood.com/blogs/voedingstips/tagged/menopause",
        }
      );
    }
  }

  // Add the rest of the plans
  plans.push(
    {
      label: t("onboarding.onboarding_7_nutrition_plan_screen.balanced"),
      description: t("onboarding.onboarding_7_nutrition_plan_screen.balanced_description"),
      value: MacrosPreferenceEnum.RECOMMENDED,
      emoji: "👍",
      testID: `nutrition-plan-option-${MacrosPreferenceEnum.RECOMMENDED}`,
    },
    {
      label: t("onboarding.onboarding_7_nutrition_plan_screen.low_carb"),
      description: t("onboarding.onboarding_7_nutrition_plan_screen.low_carb_description"),
      value: MacrosPreferenceEnum.LOW_CARB,
      emoji: "🍳",
      testID: `nutrition-plan-option-${MacrosPreferenceEnum.LOW_CARB}`,
    },
    {
      label: t("onboarding.onboarding_7_nutrition_plan_screen.keto"),
      description: t("onboarding.onboarding_7_nutrition_plan_screen.keto_description"),
      value: MacrosPreferenceEnum.KETO,
      emoji: "🥑",
      testID: `nutrition-plan-option-${MacrosPreferenceEnum.KETO}`,
    },
    {
      label: t("onboarding.onboarding_7_nutrition_plan_screen.modify"),
      description: t("onboarding.onboarding_7_nutrition_plan_screen.modify_description"),
      value: MacrosPreferenceEnum.CHOOSE_YOUR_OWN,
      emoji: "✏️",
      testID: `nutrition-plan-option-${MacrosPreferenceEnum.CHOOSE_YOUR_OWN}`,
    }
  );

  // Filter function to remove Keto if plant-based app feature flag is active
  const filterKetoForPlantBased = (plan: Plan): boolean => !isPlantBasedApp || plan.value !== MacrosPreferenceEnum.KETO;

  // Filter function to remove Low Carb if disable low carb option feature flag is active
  const filterLowCarbIfDisabled = (plan: Plan): boolean =>
    !disableLowCarbOption || plan.value !== MacrosPreferenceEnum.LOW_CARB;

  // Filter function to remove Keto if disable keto option feature flag is active
  const filterKetoIfDisabled = (plan: Plan): boolean => !disableKetoOption || plan.value !== MacrosPreferenceEnum.KETO;

  // Apply both filters to the plans array by chaining
  const finalFilteredPlans = plans
    .filter(filterKetoForPlantBased)
    .filter(filterLowCarbIfDisabled)
    .filter(filterKetoIfDisabled);

  if (!diyFormValues?.mss) {
    // NOTE: This is actually an error state if it persists.
    console.log("diyFormValues.mss is not set");

    // We use this loading to show something while the useEffect is working
    return (
      <Box flex={1} justifyContent="center" alignItems="center" bg="primary.10">
        <Spinner size="lg" color="primary.50" />
        <Text mt={4} fontSize="16">
          Loading...
        </Text>
      </Box>
    );
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const initialValues: FormSchema = {
    selectedPlan: onboardingData.dietPreferences.dietType,
  };

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <Box flex={1} bg="primary.10">
        <VStack flex={1}>
          {/* Header */}
          <HStack alignItems="center" p={4}>
            {!isThisTheFirstScreenOfTheOnboardingTheUserIsSeeing && (
              <Pressable onPress={() => navigation.goBack()} testID="back-button">
                <Icon as={Ionicons} name="arrow-back" size="6" color="function.50" />
              </Pressable>
            )}
          </HStack>

          <Formik
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            initialValues={validationSchema.cast(initialValues)}
            validationSchema={validationSchema}
            onSubmit={(values: FormSchema) => {
              const menstrualDietPlanSelected = values.selectedPlan === MacrosPreferenceEnum.CYCLE_DIET;

              if (menstrualDietPlanSelected) {
                navigation.navigate(Routes.MenstrualCycleDatesScreen);
              } else {
                // The user has not chosen a menstrual cycle plan so we will clear this information
                // NOTE: If the user then taps back and chooses a cycle diet the values will have been cleared.
                // This will not pose any big problem, but they will need to enter the values again.
                dispatch(
                  setMenstrualCycle({
                    lastMenstrualPeriodStartDate: undefined,
                    menstrualCycleLength: undefined,
                  })
                );

                navigation.navigate(Routes.MealTimesScreen);
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <VStack flex={1}>
                {/* Content */}
                <ScrollView flex={1}>
                  <VStack alignItems="center" p={4} space={4} pb={20}>
                    {/* Logo or Icon */}
                    {logoUri ? (
                      <Image
                        source={{ uri: logoUri }}
                        style={{ width: Scale(160), height: VerticalScale(60) }}
                        resizeMode="contain"
                        alt="Organization Logo"
                      />
                    ) : (
                      <Icon as={MaterialCommunityIcons} name="robot" size="16" color="primary.50" />
                    )}

                    {/* Title */}
                    <Text fontSize="24" fontWeight="bold" textAlign="center" mt={4}>
                      {t("onboarding.onboarding_7_nutrition_plan_screen.title")}
                    </Text>

                    {/* Macro Container */}
                    <HStack justifyContent="space-between" width="100%" space={2}>
                      <VStack
                        alignItems="center"
                        flex={1}
                        p={4}
                        bg="primary.10"
                        borderRadius={theme.borderRadii.card.default}
                        shadow="2"
                        testID="macro-box-calories"
                      >
                        <Text fontSize="14" color="function.30">
                          {t("general.calories")}
                        </Text>
                        <Text fontSize="18" fontWeight="bold" my={1}>
                          {calculateDailyTotalForMacro("kcal", diyFormValues.mss)}
                        </Text>
                        <Text fontSize="24">🔥</Text>
                      </VStack>

                      <VStack
                        alignItems="center"
                        flex={1}
                        p={4}
                        bg="primary.10"
                        borderRadius={theme.borderRadii.card.default}
                        shadow="2"
                        testID="macro-box-protein"
                      >
                        <Text fontSize="14" color="function.30">
                          {t("general.protein_short")}
                        </Text>
                        <Text fontSize="18" fontWeight="bold" my={1}>
                          {calculateDailyTotalForMacro("protein", diyFormValues.mss)}
                        </Text>
                        <Text fontSize="24">💪</Text>
                      </VStack>

                      <VStack
                        alignItems="center"
                        flex={1}
                        p={4}
                        bg="primary.10"
                        borderRadius={theme.borderRadii.card.default}
                        shadow="2"
                        testID="macro-box-fat"
                      >
                        <Text fontSize="14" color="function.30">
                          {t("general.fat")}
                        </Text>
                        <Text fontSize="18" fontWeight="bold" my={1}>
                          {calculateDailyTotalForMacro("fat", diyFormValues.mss)}
                        </Text>
                        <Text fontSize="24">🥑</Text>
                      </VStack>

                      <VStack
                        alignItems="center"
                        flex={1}
                        p={4}
                        bg="primary.10"
                        borderRadius={theme.borderRadii.card.default}
                        shadow="2"
                        testID="macro-box-carbs"
                      >
                        <Text fontSize="14" color="function.30">
                          {t("general.carbohydrates_short")}
                        </Text>
                        <Text fontSize="18" fontWeight="bold" my={1}>
                          {calculateDailyTotalForMacro("carbohydrates", diyFormValues.mss)}
                        </Text>
                        <Text fontSize="24">🍞</Text>
                      </VStack>
                    </HStack>

                    {/* Plan Options */}
                    <VStack space={2} width="100%">
                      {finalFilteredPlans.map((plan) => {
                        const isSelected = values.selectedPlan === plan.value;
                        return (
                          <Pressable
                            key={plan.value}
                            onPress={() => {
                              setFieldValue("selectedPlan", plan.value);

                              if (plan.value === "CHOOSE_YOUR_OWN") {
                                navigation.navigate(Routes.ModifyNutritionalPlanScreen);
                              } else {
                                // NOTE: We don't do this for CHOOSE_YOUR_OWN because this is done in the Modify screen
                                // This is important because at this point the user hasn't chosen their macros

                                // Update the state with the new diet preferences
                                const updatedDietPreferences: DietPreferences = {
                                  ...onboardingData.dietPreferences,
                                  dietType: plan.value,
                                };
                                dispatch(onboardingSlice.actions.setDietPreferences(updatedDietPreferences));

                                // We also need to update the state with the new macros
                                // if the user selected a different diet
                                // Note that we need to update the onboarding data
                                // locally because it may not have been
                                // updated since the dispatch above

                                const updatedOnboardingData = {
                                  ...onboardingData,
                                  dietPreferences: updatedDietPreferences,
                                };
                                updateMacrosInState(dispatch, updatedOnboardingData);
                              }
                            }}
                          >
                            <Box
                              flexDirection="row"
                              alignItems="center"
                              p={4}
                              borderRadius={theme.borderRadii.card.default}
                              borderWidth={1}
                              borderColor={isSelected ? "primary.50" : "function.20"}
                              bg={isSelected ? "primary.20" : "primary.10"}
                              width="100%"
                              minHeight="70px"
                              testID={plan.testID}
                            >
                              {/* Text Container */}
                              <VStack flex={1} ml={4}>
                                <Text fontSize="18" fontWeight="bold" color={isSelected ? "primary.50" : "function.50"}>
                                  {plan.emoji} {plan.label}
                                </Text>
                                <Text fontSize="14" color="function.30" width="80%" flexWrap="wrap">
                                  {plan.description}
                                </Text>
                              </VStack>

                              {/* Info Icon */}
                              {plan.moreInfoUrl ? (
                                <Pressable
                                  onPress={() => {
                                    if (!plan.moreInfoUrl) {
                                      throw new Error("plan.moreInfoUrl is not set");
                                    }
                                    Linking.openURL(plan.moreInfoUrl).catch((err) => {
                                      throw err;
                                    });
                                  }}
                                  mr={2}
                                >
                                  <Icon as={FontAwesome5} name="info-circle" size="6" color="primary.50" />
                                </Pressable>
                              ) : null}

                              {/* Check Icon */}
                              {isSelected ? (
                                <Icon as={FontAwesome5} name="check-circle" size="6" color="primary.50" />
                              ) : null}
                            </Box>
                          </Pressable>
                        );
                      })}
                    </VStack>
                  </VStack>
                </ScrollView>

                {/* Bottom Button */}
                <Box p={4} bg="primary.10">
                  <Button
                    onPress={() => handleSubmit()}
                    bg="primary.50"
                    _pressed={{ bg: "primary.50:alpha.80" }}
                    py={3}
                    borderRadius={theme.borderRadii.button.default}
                    width="100%"
                    testID="next-button"
                  >
                    <Text color="primary.10" fontWeight="bold">
                      {t("onboarding.onboarding_7_nutrition_plan_screen.next_button")}
                    </Text>
                  </Button>
                </Box>
              </VStack>
            )}
          </Formik>
        </VStack>
      </Box>
    </SafeAreaView>
  );
};

export default NutritionalPlanScreen;
